import React, {useEffect, useMemo, useState} from 'react';
import {Button} from '@/ui-kits';
import {AccordianLayout} from '../AccordianLayout';
import {StepProps} from './types';
import {ConsulationFlowType, Images, paymentSuccessTimer} from '@/constants';
import {
  patientDashboardConsultation,
  patientDashboardLoadingStatus,
  patientDashboardPaymentAmount,
  patientDashboardPaymentStatus,
  patientDashboardPaymentUrl,
  patientDashboardSpecialistSelected,
  patientDashboardPaymentTimerStatus,
  accountDeletionStatusSelector,
  patientDashboardConsultationFlow,
} from '@/services/redux/selectors';
import {consultationPaymentRequest, resetPaymentUrl, setPatientDashboardMessage, moveToStep4} from '@/services/redux/slices';
import {useDispatch, useSelector} from 'react-redux';
import {appendPlural, formatName} from '@/utils/common_functions';
import {useHandleUrlStatus, useSecondsTimer} from '@/hooks';
import {PatientAccountTicketRequestStatus} from '@/constants';

export const PaymentProcess: React.FC<StepProps> = ({open, success}) => {
  const payment_amount = useSelector(patientDashboardPaymentAmount);
  const specialist = useSelector(patientDashboardSpecialistSelected);
  const consultation_id = useSelector(patientDashboardConsultation) as number;
  const payment_url = useSelector(patientDashboardPaymentUrl);
  const payment_status = useSelector(patientDashboardPaymentStatus);
  const isLoading = useSelector(patientDashboardLoadingStatus);
  const dispatch = useDispatch();
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);
  const consultationFlow = useSelector(patientDashboardConsultationFlow);
  // function handlers

  const handleNext = () => {
    if (payment_status === 'success') {
      dispatch(moveToStep4());
    }
  };

  const handleProceedToPay = () => {
    if (!consultation_id) return;

    dispatch(
      consultationPaymentRequest({
        id: consultation_id,
        payment_method: 'card',
        redirect_url: window.location.href,
      }),
    );
  };

  /**
   * side effect handlers
   */
  // handle url status
  useHandleUrlStatus({
    onSuccess: success => {
      dispatch(setPatientDashboardMessage({status: 'success', statusCode: success}));
    },
    onError: error => {
      dispatch(setPatientDashboardMessage({status: 'error', statusCode: error}));
    },
  });

  // handle payment
  useEffect(() => {
    if (payment_url) {
      dispatch(resetPaymentUrl());
      window.location.href = payment_url;
    }
  }, [payment_url]);

  return (
    <AccordianLayout
      icon={consultationFlow !== ConsulationFlowType.HospitalSpecialistAssigned ? 'circle_3' : 'circle_2'}
      title="Payment Process"
      key="Payment Process"
      open={open}
      success={success}>
      <div className="pb-6 sm:pb-8 border-t">
        {!payment_status ? (
          /* Payment Summary */
          <div className="bg-white rounded-xl py-5 shadow-sm">
            <div className="border shadow-lg mx-2 mb-6 py-3 px-4 rounded-lg">
              {/* Summary Heading */}
              <h3 className="text-xl font-semibold text-ui_blue mb-4">Summary</h3>

              {/* Selected Specialist */}
              <div className="flex justify-between mb-3 font-FigtreeBold text-ui_blue text-base sm:text-lg">
                <span className="font-FigtreeRegular">Selected {consultationFlow === ConsulationFlowType.HospitalSpecialistAssigned ? "Hospital" : "Specialist"}:</span>
                <span>
                  {consultationFlow === ConsulationFlowType.HospitalSpecialistAssigned ? "" : "Dr. "}
                  {formatName(specialist.name)}
                  </span>
              </div>

              {/* Total Cost */}
              <div className="flex justify-between font-bold text-ui_blue text-base sm:text-lg mb-5 border-t pt-3">
                <span>Total Cost:</span>
                <span className="border shadow-lg py-1 px-3 rounded">₹ {payment_amount}</span>
              </div>
            </div>
            {/* Buttons */}
            {accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && (
              <div className="flex flex-col md:flex-row justify-center gap-3">
                <Button
                  title="Proceed to Payment"
                  onClick={handleProceedToPay}
                  className="w-full md:w-[200px] flex-nowrap"
                  isLoading={isLoading}
                  disabled={isLoading}
                />
              </div>
            )}
          </div>
        ) : (
          /* Payment Success Screen - Matching Figma Design */
          accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && (
            <PaymentStatus stepSuccess={success ?? false} handleNext={handleNext} handleTryAgain={handleProceedToPay} />
          )
        )}
      </div>
    </AccordianLayout>
  );
};

interface PaymentStatusProps {
  handleNext: () => void;
  handleTryAgain: () => void;
  stepSuccess: boolean;
}
const PaymentStatus: React.FC<PaymentStatusProps> = props => {
  const {handleNext, handleTryAgain, stepSuccess} = props;
  const payment_amount = useSelector(patientDashboardPaymentAmount);
  const payment_status = useSelector(patientDashboardPaymentStatus);
  const paymentLastUpdate = useSelector(patientDashboardPaymentTimerStatus);
  const remainingTime = useSecondsTimer({
    refferenceTimeStamp: paymentLastUpdate as string,
    timerLimit: paymentSuccessTimer,
    onTimerEnd: handleNext,
  });

  const is_payment_success = useMemo(() => payment_status === 'success', [payment_status]);

  const buttonTextPrePayment = useMemo<string>(() => {
    if (!payment_status || stepSuccess) return 'Next';

    if (payment_status == 'success') {
      return `Proceeding in ${remainingTime} ${appendPlural('sec', remainingTime)}`;
    }

    return 'Try Again';
  }, [remainingTime, payment_status]);

  return (
    <div className="bg-white flex flex-col rounded-xl text-center py-8 px-4">
      {/* Success Icon - Three Concentric Circles with Checkmark */}
      <div className="flex justify-center mb-6">
        <img
          alt={is_payment_success ? 'payment_success' : 'payment_failed'}
          src={is_payment_success ? Images.payment_success : Images.payment_failure}
          className="w-[120px] lg:w-[155px] aspect-[31/35] object-contain"
        />
      </div>

      {/* Success Message */}
      <h3 className="text-xl sm:text-2xl font-FigtreeBold text-ui_blue mb-2">{is_payment_success ? 'Payment Successful' : 'Payment Failed'}</h3>

      {/* Amount Paid */}
      <p className="text-ui_input_border_dark font-FigtreeRegular text-xl mb-6">
        {is_payment_success ? `Amount Paid ₹ ${payment_amount}` : 'Payment not completed. Please try again.'}
      </p>

      {/* Next Button - Gradient Blue (Active) */}
      {!stepSuccess && <Button
        disabled={stepSuccess || remainingTime > 0}
        title={buttonTextPrePayment}
        onClick={is_payment_success ? handleNext : handleTryAgain}
        className="w-full text-nowrap md:w-[180px] self-center"
      />}
    </div>
);
};
